// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Reference to a parent record across modules. Used for the cross-module
 * Ask-Forseti integration (47.5.8). Type values match the corresponding
 * module's audit `resource_type`.
 */
/**
 * Sprint 47.5.8 — added 'incident_op' so the cross-module loop can scope
 * Forseti chats to incident-ops playbook activations.
 *
 * Sprint 47.5.8.4a — added 'dossier_filing' and 'transfer_assessment' so
 * the cross-module loop covers the full Privacy lane (DPIA + Dossier Filings
 * + Transfer Assessments + RoPA Activity + Rights Request).
 *
 * Sprint 47.5.8.4b — added 'vendor_contract', 'vendor_acceptance',
 * 'breach_notification', 'authority_request', 'supplement_request' for
 * the Risk + Vendor lane (Vendor + 3 vendor sub-records + 4 incident
 * sub-records).
 *
 * Sprint 62.10.0 — added the ten records of the 62.x sector-domain track.
 *
 * The ten are **derived, not chosen.** A record earns record-scoped Forseti
 * assistance where the domain service already enforces a human decision for
 * Forseti to feed and never bypass — and a decision the platform enforces has a
 * permission, so the population is enumerable rather than a matter of taste.
 * Ten such permissions exist across the six domains, each traced to the route
 * that requires it and the record that route decides on:
 *
 *   ai_system_profile        ← ai_governance:review
 *   open_data_release        ← public_data:release
 *   retention_reconciliation ← payments_evidence:reconcile
 *   platform_correspondence  ← platform_evidence:respond
 *   conformity_record        ← manufacturing_conformity:declare
 *   capa_record              ← manufacturing_recall:approve_closure
 *   epr_submission           ← manufacturing_epr:submit
 *   remote_access_grant      ← manufacturing_ot:approve_remote_access
 *   parcel_claim             ← logistics_claim:decide
 *   postal_authorisation     ← logistics_authorisation:submit
 *
 * That derivation is what makes AC3 and AC4 structural: there is no record here
 * on which Forseti could be the last actor, because every one of them already
 * has a person at the end of it.
 *
 * `platform_correspondence` is deliberately **not** the shipped
 * `authority_request`. Sprint 62.7.0 annotates that register rather than
 * replacing it, so the two are different records and must not share a slug.
 *
 * Adding a member here is not a local edit. Five registries key on this union:
 * `COLLECTION_BY_RECORD_TYPE`, `TENANT_KEY_BY_RECORD_TYPE`, `LOCKED_STATUSES`
 * and `RECORD_ADVISOR_CONFIG` are `Record<ParentRecordType, …>` and fail the
 * build; `attach-evidence.ts`'s target union and the api-client's
 * `getAdvisorSummaryForRecord` switch do **not**, and are pinned by
 * set-equality tests instead. See the 62.10.0 work file's C.1 trace.
 */
export const PARENT_RECORD_TYPES = [
    'project',
    'dpia',
    'dossier_filing',
    'transfer_assessment',
    'vendor',
    'vendor_contract',
    'vendor_acceptance',
    'incident',
    'incident_op',
    'breach_notification',
    'authority_request',
    'supplement_request',
    'rights_request',
    'contract',
    'task',
    'data_mapping_activity',
    'compliance_rule',
    'program',
    'governance_document',
    'remediation_plan',
    'cross_border_filing',
    // ── Sprint 62.10.0: the 62.x sector-domain track ──────────────
    'ai_system_profile',
    'open_data_release',
    'retention_reconciliation',
    'platform_correspondence',
    'conformity_record',
    'capa_record',
    'epr_submission',
    'remote_access_grant',
    'parcel_claim',
    'postal_authorisation',
    // The three the first census missed. Recorded in the 62.10.0 work file as my
    // error, not as a late addition: the criterion above was right and the grep
    // that applied it was not. Its prefix list omitted `TRUST_EVIDENCE_`, and its
    // keyword list carried `submit` and `declare` but not `lodge` — so a
    // permission gating a filing with a customs authority was invisible to a
    // search for decisions. Unfiltered, the seven domains hold 17 non-CRUD
    // permissions, not 10. A keyword list is a list of instances already seen.
    'customs_record', //        ← logistics_customs:lodge
    'electronic_submission', // ← trust_evidence:submit
    'trust_credential', //      ← trust_evidence:lifecycle
    /**
     * Sprint 64.167.0 — the official handbook evidence checklist.
     *
     * It was the one record in Program Governance whose entire purpose is
     * "assemble evidence and have a human review it", and the only one Forseti
     * could not see. Maturity assessments reuse `program`; the checklist cannot,
     * because `program` resolves to `grc_maturity_assessments` and the two are
     * different collections with different lifecycles.
     */
    'handbook_assessment',
    /**
     * Sprint 62.20.0 — the regulatory classification subject profile.
     *
     * ONE type gives AC7 its whole surface. The profile is the record that
     * carries all three of a subject's classifications side by side, so an
     * advisor scoped to it can cite the data-security risk level, the importance
     * level and the information-system security level **separately, with their
     * own sources**, which is exactly what AC7 asks and what a per-scheme record
     * type could not do without inviting a merged reading.
     *
     * `transfer_assessment` is already a member, so AC3's screen already has an
     * advisor surface and needs nothing here. `data_registry_item` and
     * `grc_system` were considered and declined: the canonical records carry no
     * classification of their own (`GrcSystem` has no tier field at all), so an
     * advisor over them would have to join to this profile anyway — and would
     * then hold two record types that answer one question.
     */
    'classification_subject_profile',
    /**
     * Sprint 63.22.0 (OD-A5) — every 62.13+ sector register that has a
     * single-record detail view in `App.tsx`. The criterion is the route, so the
     * population is enumerable: ten registers have a `/:id` detail page, and the
     * fifteen others in the spec's gap table are list-only (MCP reach through
     * `search_module`, no record-scoped chat). Each was measured at E0 against
     * its module's own read path: all ten read `findOne({ org_id, _id })`.
     */
    'enforcement_case', //         ← /admin/enforcement-cases/:id
    'authority_direction', //      ← /admin/authority-directions/:id
    'regulated_account', //        ← /admin/regulated-accounts/:id
    'regulatory_placeholder', //   ← /admin/regulatory-placeholders/:id
    'sector_licence', //           ← /admin/sector-licences/:id
    'credit_information_supply', // ← /admin/credit-information-supply/:supplyId
    'data_service_provider', //    ← /admin/data-service-providers/:providerId
    'regulatory_disclosure', //    ← /admin/regulatory-disclosures/:id
    'issued_record', //            ← /admin/student-records/:recordId
    'publication', //              ← /admin/publications/:id
];
/** Hard cap for `AdvisorSummary.summary` — asserted at every adapter. */
export const ADVISOR_SUMMARY_MAX_CHARS = 400;
/**
 * fix-sprint-20260506-2121-followup verifier round 1 — the longest Forseti chat
 * turn the server accepts (`chat-schemas.ts`) and the composer allows, one
 * figure for both. It bounds the whole outgoing text, so a document the review
 * modal sends as context counts towards it.
 */
export const MAX_CHAT_MESSAGE_CHARS = 20_000;
//# sourceMappingURL=ai-advisor.js.map